/**
 * Card → detail bridge — MOCK STAGE ONLY.
 *
 * The acquisition Campaigns / operations Promotions cards each carry their own
 * identity (title, artwork, period), but the detail templates render from a single
 * shared mock payload — the "Halloween" sample. So every card opened the same
 * Halloween detail regardless of what was tapped.
 *
 * Until these pages are wired to an API (the detail endpoint will return the full
 * payload per id), these helpers stamp the clicked card's identity onto the shared
 * mock so the detail page reads as the campaign/promotion the owner actually
 * tapped. Detail-only fields the card has no source for (subtitle, KPIs, assets,
 * offering copy) stay as the sample data.
 *
 * Delete alongside the mock-data files when the detail endpoints land — the API
 * response already carries its own title/image/period (see docs/mock-data.md).
 */
import type { RunningCampaignData, SuggestedCampaignData } from '../mock-data/types';

/**
 * The identity fields a campaign/promotion card renders (its period, thumbnail and
 * title). Both `Campaign` and `Promotion` satisfy this shape structurally.
 */
export type CardIdentity = {
  title: string;
  image: string;
  period: string;
};

/**
 * Stamp the clicked card's identity onto a Running / paused / ended detail payload:
 * the hero's title + artwork, and the period as the "Duration" value. Everything
 * else (subtitle, offering, KPIs, assets, crosslink, endedOn) is preserved.
 */
export const withRunningCardIdentity = (data: RunningCampaignData, card: CardIdentity): RunningCampaignData => ({
  ...data,
  hero: {
    ...data.hero,
    title: card.title,
    image: card.image,
    duration: { ...data.hero.duration, value: card.period },
  },
});

/**
 * Stamp the clicked card's identity onto a Suggested detail payload: the campaign
 * name becomes the offering headline and the card period drives the period label.
 * The remaining offering copy / channels / style options stay as the sample data.
 */
export const withSuggestedCardIdentity = (data: SuggestedCampaignData, card: CardIdentity): SuggestedCampaignData => ({
  ...data,
  periodLabel: `Campaign Period: ${card.period}`,
  offering: { ...data.offering, title: card.title },
});
